import i18next from 'i18next';
import HttpBackend from 'i18next-http-backend';
import React from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';

export const DEFAULT_I18N_BOOTSTRAP = Object.freeze({
  uiLocale: 'en',
  fallbackLocale: 'en',
  direction: 'ltr',
  resourceUrl: '/locales/{{lng}}/{{ns}}.json',
});

const requestWithTimeout = (fetchImpl = globalThis.fetch, timeoutMs = 5000) => (
  options,
  url,
  payload,
  callback,
) => {
  if (!fetchImpl) {
    callback(new Error('Catalog fetch is unavailable'), { status: 0, data: '' });
    return;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  fetchImpl(url, {
    ...options,
    body: payload || undefined,
    signal: controller.signal,
  })
    .then(async response => {
      const data = await response.text();
      if (!response.ok) {
        throw new Error(`Catalog request failed with status ${response.status}`);
      }
      callback(null, { status: response.status, data });
    })
    .catch(error => callback(error, { status: 0, data: '' }))
    .finally(() => clearTimeout(timeout));
};

const createEnglishFallback = async () => {
  const fallback = i18next.createInstance();
  fallback.use(initReactI18next);
  await fallback.init({
    fallbackLng: 'en',
    lng: 'en',
    ns: ['common'],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    resources: { en: { common: {} } },
  });
  return fallback;
};

export const createBrowserI18n = async ({
  bootstrap = globalThis.window?.__I18N__ || DEFAULT_I18N_BOOTSTRAP,
  fetchImpl = globalThis.fetch,
  timeoutMs = 5000,
} = {}) => {
  const settings = { ...DEFAULT_I18N_BOOTSTRAP, ...bootstrap };
  const instance = i18next.createInstance();
  instance.use(HttpBackend).use(initReactI18next);

  try {
    await instance.init({
      backend: {
        loadPath: settings.resourceUrl,
        request: requestWithTimeout(fetchImpl, timeoutMs),
      },
      defaultNS: 'common',
      fallbackLng: settings.fallbackLocale,
      interpolation: { escapeValue: false },
      lng: settings.uiLocale,
      lowerCaseLng: true,
      maxRetries: 0,
      ns: ['common'],
      react: { useSuspense: false },
      supportedLngs: [settings.fallbackLocale, settings.uiLocale],
    });
    if (!instance.hasResourceBundle(settings.uiLocale, 'common')) {
      throw new Error(`Common catalog failed to load for ${settings.uiLocale}`);
    }
    return instance;
  } catch (error) {
    console.error('Platform translation catalog failed to load; using English fallback', {
      event: 'platform_i18n_resource_failure',
      locale: settings.uiLocale,
      namespace: 'common',
      reason: error?.name || 'load_failed',
    });
    return createEnglishFallback();
  }
};

let browserI18nPromise;

export const initializeBrowserI18n = () => {
  if (!browserI18nPromise) {
    browserI18nPromise = createBrowserI18n();
  }
  return browserI18nPromise;
};

export function PlatformI18nProvider({ children, i18n }) {
  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
